import { useTranslation } from "react-i18next"

import { Card, CardContent } from "@/components/ui/card"
import { formatCompactNumber } from "@/lib/format-compact-number"
import type { ProfileImpact } from "@/types/xp"

/**
 * OKS-513 — §11 brand-impact card (card 5).
 *
 * Header "YOUR IMPACT FOR {BRAND}" over a three-up stat row: posts · views
 * · cheers (active-crew aggregate). **Views are deferred** (TRD KD-7 — no
 * membership-scoped content-view source): when `impact.views` is null the
 * views stat is omitted entirely (a two-up row) rather than showing a
 * fabricated number. Large counts abbreviate (56K, 1.2M).
 */
interface BrandImpactCardProps {
  brandName: string
  impact: ProfileImpact
}

export function BrandImpactCard({ brandName, impact }: BrandImpactCardProps) {
  const { t } = useTranslation()

  const stats: { key: string; value: number; label: string }[] = [
    { key: "posts", value: impact.posts, label: t("profile.impact.posts") },
  ]
  if (impact.views !== null) {
    stats.push({
      key: "views",
      value: impact.views,
      label: t("profile.impact.views"),
    })
  }
  stats.push({
    key: "cheers",
    value: impact.cheers,
    label: t("profile.impact.cheers"),
  })

  return (
    <Card data-testid="brand-impact-card">
      <CardContent className="flex flex-col gap-4 py-5">
        <h2 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          {t("profile.impact.heading", { brand: brandName })}
        </h2>
        <dl className="flex items-stretch justify-between gap-2 text-center">
          {stats.map((stat) => (
            <div
              key={stat.key}
              data-testid={`brand-impact-${stat.key}`}
              className="flex min-w-0 flex-1 flex-col"
            >
              <dt className="text-muted-foreground order-2 text-xs">
                {stat.label}
              </dt>
              <dd className="order-1 text-xl font-bold tabular-nums">
                {formatCompactNumber(stat.value)}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  )
}
